import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  AlertController,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { PosThemeMode, WorkspaceSummary } from '@adminpages/domain-models';
import { PosStoreService } from '@adminpages/pos-state';
import { PosPrinterConfig, PosPrintingService } from '@adminpages/pos-printing';

@Component({
  selector: 'foco-pos-settings-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonSegment,
    IonSegmentButton,
    IonSelect,
    IonSelectOption,
    IonText,
    IonTitle,
    IonToggle,
    IonToolbar,
  ],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button [routerLink]="store.selectedWorkspace().homeRoute">
            <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>Configuración</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content class="pos-page settings-content">
      <main class="settings-shell">
        <section class="settings-card">
          <p>Workspace actual</p>
          <h1>{{ store.selectedWorkspace().name }}</h1>
          <span>{{ store.activeTheme().name }} · cash / transfer</span>
          @if (store.workspaces().length > 1) {
            <div class="settings-actions">
              <ion-button routerLink="/workspace" fill="outline">
                <ion-icon name="swap-horizontal-outline" slot="start"></ion-icon>
                Cambiar workspace
              </ion-button>
            </div>
          }
        </section>

        <section class="settings-card">
          <p>Caja del dispositivo</p>
          @if (store.selectedCashRegister(); as cashRegister) {
            <h1>{{ cashRegister.name }}</h1>
            <span>
              {{ cashRegister.location || cashRegister.code }}
              · {{ cashRegister.currentSession ? 'Sesión abierta' : 'Sin sesión abierta' }}
            </span>
          } @else {
            <h1>Sin caja seleccionada</h1>
          }
          @if (store.cashRegisters().length > 1 || !store.selectedCashRegister()) {
            <div class="settings-actions">
              <ion-button routerLink="/cash-register" fill="outline">
                <ion-icon name="cash-outline" slot="start"></ion-icon>
                Cambiar caja
              </ion-button>
            </div>
          }
        </section>

        <section class="settings-card">
          <p>Tema visual</p>
          <ion-segment
            class="theme-mode-segment"
            [value]="store.themeMode()"
            (ionChange)="setThemeMode($event.detail.value)"
          >
            <ion-segment-button value="light">
              <ion-label>Claro</ion-label>
            </ion-segment-button>
            <ion-segment-button value="dark">
              <ion-label>Oscuro</ion-label>
            </ion-segment-button>
            <ion-segment-button value="system">
              <ion-label>Sistema</ion-label>
            </ion-segment-button>
          </ion-segment>

          <div class="theme-grid">
            @for (workspace of store.workspaces(); track workspace.id) {
              <button
                type="button"
                class="theme-card"
                [class.active]="workspace.id === store.selectedWorkspace().id"
                (click)="select(workspace)"
              >
                <span class="swatches">
                  <i [style.background]="theme(workspace).primaryColor"></i>
                  <i [style.background]="theme(workspace).accentColor"></i>
                  <i [style.background]="theme(workspace).backgroundColor"></i>
                </span>
                <strong>{{ workspace.name }}</strong>
                <small>{{ theme(workspace).name }}</small>
              </button>
            }
          </div>
        </section>

        <section class="settings-card">
          <p>Impresora térmica</p>
          @if (printing.printerConfig(); as printer) {
            <dl>
              <div>
                <dt>Dispositivo</dt>
                <dd>{{ printer.name }}</dd>
              </div>
              <div>
                <dt>Servicio BLE</dt>
                <dd>{{ printer.serviceUuid }}</dd>
              </div>
              <div>
                <dt>Característica</dt>
                <dd>{{ printer.characteristicUuid }}</dd>
              </div>
            </dl>

            <div class="printer-grid">
              <ion-item>
                <ion-label position="stacked">Ancho papel</ion-label>
                <ion-select [ngModel]="printer.paperWidth" (ngModelChange)="updatePaperWidth($event)">
                  <ion-select-option value="58mm">58mm</ion-select-option>
                  <ion-select-option value="80mm">80mm</ion-select-option>
                </ion-select>
              </ion-item>

              <ion-item>
                <ion-label position="stacked">Modo escritura</ion-label>
                <ion-select [ngModel]="printer.writeMode" (ngModelChange)="updateWriteMode($event)">
                  <ion-select-option value="withoutResponse">Sin respuesta</ion-select-option>
                  <ion-select-option value="withResponse">Con respuesta</ion-select-option>
                </ion-select>
              </ion-item>

              <ion-item>
                <ion-label position="stacked">Chunk bytes</ion-label>
                <ion-input type="number" [ngModel]="printer.chunkSize" (ngModelChange)="updateNumber('chunkSize', $event)"></ion-input>
              </ion-item>

              <ion-item>
                <ion-label position="stacked">Pausa ms</ion-label>
                <ion-input type="number" [ngModel]="printer.chunkDelayMs" (ngModelChange)="updateNumber('chunkDelayMs', $event)"></ion-input>
              </ion-item>
            </div>

            <div class="toggle-list">
              <ion-item>
                <ion-toggle [checked]="printer.autoPrintReceipt" (ionChange)="updateBoolean('autoPrintReceipt', $event.detail.checked)">
                  Auto imprimir al cobrar
                </ion-toggle>
              </ion-item>
              <ion-item>
                <ion-toggle [checked]="printer.cashDrawerEnabled" (ionChange)="updateBoolean('cashDrawerEnabled', $event.detail.checked)">
                  Abrir cajón con ticket
                </ion-toggle>
              </ion-item>
            </div>
          } @else {
            <span>
              Sin impresora directa configurada. El POS usará impresión del sistema como fallback.
            </span>
          }

          @if (!printing.canUseBle()) {
            <ion-text color="medium">La búsqueda BLE requiere abrir el POS como app instalada.</ion-text>
          }

          @if (printing.statusMessage(); as message) {
            <ion-text color="success">{{ message }}</ion-text>
          }

          @if (printing.errorMessage(); as error) {
            <ion-text color="danger">{{ error }}</ion-text>
          }

          <div class="settings-actions printer-actions">
            <ion-button [disabled]="printing.busy() || !printing.canUseBle()" (click)="configurePrinter()">
              Buscar impresora BLE
            </ion-button>
            <ion-button fill="outline" [disabled]="printing.busy() || !printing.printerConfig()" (click)="printTest()">
              Imprimir prueba
            </ion-button>
            <ion-button fill="outline" color="danger" [disabled]="printing.busy() || !printing.printerConfig()" (click)="printing.clearConfig()">
              Eliminar
            </ion-button>
          </div>
        </section>

        <section class="settings-card">
          <p>Conexión API</p>
          <dl>
            <div>
              <dt>Login</dt>
              <dd>POST /api/v1/pos/login</dd>
            </div>
            <div>
              <dt>Productos</dt>
              <dd>GET /api/v1/pos/products</dd>
            </div>
            <div>
              <dt>Venta</dt>
              <dd>POST /api/v1/pos/orders</dd>
            </div>
          </dl>
        </section>

        <section class="settings-card logout-card">
          <p>Sesión</p>
          <h1>Cerrar sesión</h1>
          <span>Revoca el acceso de este dispositivo y regresa a la pantalla de ingreso.</span>

          @if (store.logoutErrorMessage(); as error) {
            <ion-text color="danger">{{ error }}</ion-text>
          }

          <div class="settings-actions">
            <ion-button
              color="danger"
              fill="outline"
              [disabled]="store.logoutLoading()"
              (click)="confirmLogout()"
            >
              <ion-icon name="log-out-outline" slot="start"></ion-icon>
              {{ store.logoutLoading() ? 'Cerrando sesión...' : 'Cerrar sesión' }}
            </ion-button>
          </div>
        </section>
      </main>
    </ion-content>
  `,
  styles: `
    ion-toolbar {
      --background: var(--pos-color-surface);
    }

    .settings-content {
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .settings-shell {
      display: grid;
      gap: 16px;
      margin: 0 auto;
      max-width: 920px;
    }

    .settings-card {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      box-shadow: 0 10px 28px var(--pos-color-shadow);
      display: grid;
      gap: 10px;
      padding: 18px;
    }

    .settings-card p,
    .settings-card h1 {
      margin: 0;
    }

    .settings-card p {
      color: var(--pos-color-primary);
      font-weight: 800;
      text-transform: uppercase;
    }

    .settings-card span,
    dd {
      color: var(--pos-color-muted);
    }

    .logout-card {
      border-color: var(--ion-color-danger);
    }

    .logout-card p {
      color: var(--ion-color-danger);
    }

    .settings-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      justify-content: flex-start;
      margin-top: 4px;
    }

    .printer-grid {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .printer-actions ion-button {
      margin: 0;
    }

    ion-item {
      --background: var(--pos-color-input);
      --border-color: var(--pos-color-border);
      --border-radius: 8px;
      --padding-start: 12px;
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
    }

    .toggle-list {
      display: grid;
      gap: 10px;
    }

    .theme-mode-segment {
      --background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      padding: 4px;
    }

    .theme-mode-segment ion-segment-button {
      --color: var(--pos-color-muted);
      --color-checked: white;
      --indicator-color: var(--pos-color-primary);
      --indicator-box-shadow: none;
      min-height: 40px;
    }

    :host-context([data-pos-theme-mode='dark']) .theme-mode-segment ion-segment-button {
      --color: var(--pos-color-muted);
      --color-checked: var(--pos-color-text);
      --indicator-color: var(--pos-color-primary);
    }

    .theme-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .theme-card {
      background: var(--pos-color-input);
      border: 2px solid transparent;
      border-radius: 8px;
      color: var(--pos-color-text);
      cursor: pointer;
      display: grid;
      gap: 8px;
      padding: 16px;
      text-align: left;
    }

    .theme-card.active {
      border-color: var(--pos-color-primary);
    }

    .swatches {
      display: flex;
      gap: 6px;
    }

    .swatches i {
      border: 1px solid var(--pos-color-border);
      border-radius: 999px;
      height: 24px;
      width: 24px;
    }

    dl,
    dl div {
      display: grid;
      gap: 6px;
    }

    dl {
      margin: 0;
    }

    dt {
      font-weight: 800;
    }

    dd {
      margin: 0;
      word-break: break-word;
    }

    @media (max-width: 640px) {
      .theme-grid,
      .printer-grid {
        grid-template-columns: 1fr;
      }
    }
  `,
})
export class SettingsPageComponent {
  private readonly router = inject(Router);
  private readonly alertController = inject(AlertController);
  readonly store = inject(PosStoreService);
  readonly printing = inject(PosPrintingService);

  async select(workspace: WorkspaceSummary): Promise<void> {
    await this.store.selectWorkspace(workspace);

    if (!this.store.cashRegisterReady()) {
      void this.router.navigateByUrl('/cash-register');
    }
  }

  setThemeMode(value: string | number | undefined): void {
    if (value === 'light' || value === 'dark' || value === 'system') {
      this.store.setThemeMode(value as PosThemeMode);
    }
  }

  theme(workspace: WorkspaceSummary) {
    return this.store.themeForWorkspace(workspace);
  }

  async confirmLogout(): Promise<void> {
    if (this.store.logoutLoading()) {
      return;
    }

    const alert = await this.alertController.create({
      header: 'Cerrar sesión',
      message: 'Se revocará el acceso de este dispositivo. La configuración local de tema, impresora, workspace y caja se conservará.',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
        },
        {
          text: 'Cerrar sesión',
          role: 'confirm',
        },
      ],
    });

    await alert.present();
    const result = await alert.onDidDismiss();

    if (result.role !== 'confirm') {
      return;
    }

    try {
      await this.store.logout();
      await this.router.navigateByUrl('/login', { replaceUrl: true });
    } catch {
      // El store conserva la sesión y expone el error para reintentar.
    }
  }

  async configurePrinter(): Promise<void> {
    try {
      await this.printing.configureBlePrinter();
    } catch {
      // El servicio expone el error para la UI.
    }
  }

  async printTest(): Promise<void> {
    try {
      await this.printing.printTest();
    } catch {
      // El servicio expone el error para la UI.
    }
  }

  updatePaperWidth(value: string | number | undefined): void {
    if (value === '58mm' || value === '80mm') {
      this.updatePrinter({ paperWidth: value });
    }
  }

  updateWriteMode(value: string | number | undefined): void {
    if (value === 'withResponse' || value === 'withoutResponse') {
      this.updatePrinter({ writeMode: value });
    }
  }

  updateNumber(key: 'chunkSize' | 'chunkDelayMs', value: string | number | null | undefined): void {
    const number = Number(value);

    if (Number.isFinite(number) && number > 0) {
      this.updatePrinter({ [key]: number } as Pick<PosPrinterConfig, typeof key>);
    }
  }

  updateBoolean(key: 'autoPrintReceipt' | 'cashDrawerEnabled', value: boolean): void {
    this.updatePrinter({ [key]: value } as Pick<PosPrinterConfig, typeof key>);
  }

  private updatePrinter(update: Partial<Pick<PosPrinterConfig, 'paperWidth' | 'writeMode' | 'chunkSize' | 'chunkDelayMs' | 'autoPrintReceipt' | 'cashDrawerEnabled'>>): void {
    const current = this.printing.printerConfig();

    if (!current) {
      return;
    }

    this.printing.saveConfig({ ...current, ...update });
  }
}
